import { ReactElement, useEffect, useState } from 'react';
import { useMessage } from '@messageformat/react';
import { Col, Row, Tab, Tabs } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import ProfitGraph from './ProfitGraph';
import ProfitSlider from './ProfitSllider';
import { useIsMobile } from '../../util/mediaQuery';

const countProfitExceptationMonth = (profitExceptation12Month: number): number => {
  return (1 + profitExceptation12Month / 100) ** (1 / 12) - 1;
};

const createProfitArray = (
  profitExpectation: number,
  initialSaving: number,
  savingTime: number,
  monthlySaving: number,
): number[] => {
  const monthlyProfit = countProfitExceptationMonth(profitExpectation);
  let finalSavingsAndProfit = initialSaving;
  return [...Array(savingTime * 12 + 1)].map((_, index) => {
    switch (index) {
      case 0:
        return initialSaving;
      default:
        finalSavingsAndProfit += monthlySaving;
        finalSavingsAndProfit *= 1 + monthlyProfit;
        return finalSavingsAndProfit;
    }
  });
};

const numberToFinnishString = (num: number): string => {
  return num.toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const ProfitCalculator = (): ReactElement => {
  const [savingAmount, setSavingAmount] = useState(50);
  const [savingTime, setSavingTime] = useState(2);
  const [initialSaving, setInitialSaving] = useState(0);
  const [profitExpectation, setProfitExpectation] = useState(2);
  const [finalSum, setFinalSum] = useState(0);
  const [profitGraphArray, setProfitGraphArray] = useState<number[][]>([]);
  const labelMsg = useMessage('course4.question3.label');
  const savingTabMsg = useMessage('course4.question3.saving_tab');
  const profitTabMsg = useMessage('course4.question3.profit_tab');
  const isMobile = useIsMobile();

  useEffect(() => {
    const createGraphData = (arr: number[]): [number, number][] => {
      const result: [number, number][] = [];
      for (let i = 0; i < arr.length; i += 12) {
        result.push([Math.floor(i / 12), arr[i]]);
      }
      return result;
    };

    const countProfitDataSet = (): void => {
      const profitArray = createProfitArray(
        profitExpectation,
        initialSaving,
        savingTime,
        savingAmount,
      );

      const roundedProfitArray: number[] = profitArray.map((num) => {
        return Math.round(num);
      });
      setFinalSum(roundedProfitArray[roundedProfitArray.length - 1]);

      const graphData = createGraphData(roundedProfitArray);
      setProfitGraphArray(graphData);
    };
    countProfitDataSet();
  }, [savingAmount, savingTime, initialSaving, profitExpectation]);

  return (
    <div>
      {isMobile ? (
        <Tabs
          small
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 0 0',
            paddingTop: space.default,
          }}
        >
          <Tab
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={savingTabMsg}
          >
            <Row style={{ margin: 0 }}>
              <p className="investoracademy-slider-label-text">{labelMsg}</p>
              <Col
                lg={6}
                sm={12}
                className="investoracademy-vertical-flex-align"
                style={{ padding: 0 }}
              >
                <ProfitSlider
                  savingAmount={savingAmount}
                  initialSaving={initialSaving}
                  savingTime={savingTime}
                  profitExpectation={profitExpectation}
                  setSavingAmount={setSavingAmount}
                  setInitialSaving={setInitialSaving}
                  setSavingTime={setSavingTime}
                  setProfitExpectation={setProfitExpectation}
                />
              </Col>
            </Row>
          </Tab>
          <Tab style={{ display: 'flex', flexDirection: 'column' }} label={profitTabMsg}>
            <div className="investoracademy-vertical-flex-align" style={{ padding: 0 }}>
              <ProfitGraph
                profitGraphArray={profitGraphArray}
                finalProfit={numberToFinnishString(finalSum)}
              />
            </div>
          </Tab>
        </Tabs>
      ) : (
        <div>
          <h4 className="investoracademy-slider-label-text">{labelMsg}</h4>
          <Row style={{ margin: 0 }}>
            <Col md={6} sm={12} className="investoracademy-profit-column-left">
              <ProfitSlider
                savingAmount={savingAmount}
                initialSaving={initialSaving}
                savingTime={savingTime}
                profitExpectation={profitExpectation}
                setSavingAmount={setSavingAmount}
                setInitialSaving={setInitialSaving}
                setSavingTime={setSavingTime}
                setProfitExpectation={setProfitExpectation}
              />
            </Col>
            <Col md={6} sm={12} className="investoracademy-profit-column-right">
              <ProfitGraph
                profitGraphArray={profitGraphArray}
                finalProfit={numberToFinnishString(finalSum)}
              />
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default ProfitCalculator;
